/**
 * 缓存管理 — GET /api/admin/cache/stats + POST /api/admin/cache/invalidate
 *
 * 展示当前被追踪前缀的 key 数量以及 Redis used_memory_human，并提供按
 * scope 清理的按钮（config / session / all）。
 */
import { Button, Card, Col, Popconfirm, Row, Space, Statistic, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface CacheStats {
  counts: Array<{ prefix: string; count: number }>;
  used_memory_human: string | null;
}

const PREFIX_LABEL: Record<string, string> = {
  'uo_config:': '平台配置',
  'jwt_blacklist:': '会话黑名单',
  'bull:': 'BullMQ 队列',
  'fastify-rate-limit:': '速率限制',
};

export function CachePage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canInvalidate = hasNode('system.cache.invalidate');

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'cache', 'stats'],
    queryFn: () => api.get<CacheStats>('/api/admin/cache/stats'),
    refetchInterval: 10000,
  });

  async function onInvalidate(scope: 'config' | 'session' | 'all') {
    try {
      const res = await api.post<{ scope: string; deleted: number }>(
        '/api/admin/cache/invalidate',
        { scope },
      );
      message.success(`已清理 ${res.deleted} 个键`);
      qc.invalidateQueries({ queryKey: ['admin', 'cache', 'stats'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '清理失败');
    }
  }

  const counts = data?.counts ?? [];
  const totalKeys = counts.reduce((sum, c) => sum + c.count, 0);

  const columns = [
    {
      title: '前缀',
      dataIndex: 'prefix',
      render: (v: string) => (
        <Space>
          <code>{v}</code>
          {PREFIX_LABEL[v] && <Tag>{PREFIX_LABEL[v]}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Key 数量',
      dataIndex: 'count',
      width: 160,
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>缓存管理</h2>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic title="Redis 内存占用" value={data?.used_memory_human ?? '—'} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="追踪键总数" value={totalKeys} loading={isFetching} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="追踪前缀数"
              value={counts.length}
              loading={isFetching}
            />
          </Card>
        </Col>
      </Row>

      {canInvalidate && (
        <Space style={{ marginBottom: 16 }} wrap>
          <Popconfirm
            title="清理平台配置缓存？"
            description="会触发所有后端副本重新加载 uo_config。"
            onConfirm={() => onInvalidate('config')}
          >
            <Button>清理配置缓存</Button>
          </Popconfirm>
          <Popconfirm
            title="清理会话黑名单？"
            description="被拉黑的 JWT 将提前恢复可用，请谨慎。"
            onConfirm={() => onInvalidate('session')}
          >
            <Button>清理会话黑名单</Button>
          </Popconfirm>
          <Popconfirm
            title="清理全部安全前缀？"
            description="包含配置与会话黑名单；不会触及 BullMQ 队列与速率限制键。"
            onConfirm={() => onInvalidate('all')}
          >
            <Button danger>清理全部</Button>
          </Popconfirm>
          <Button onClick={() => qc.invalidateQueries({ queryKey: ['admin', 'cache', 'stats'] })}>
            刷新
          </Button>
        </Space>
      )}

      <Table
        size="small"
        bordered
        rowKey="prefix"
        loading={isFetching}
        dataSource={counts}
        columns={columns}
        pagination={false}
      />
    </div>
  );
}
